import { useCallback, useEffect, useState } from 'react';
import type {
  ActivationOptionsResponse,
  AddressFieldMetadata,
  CountryStateSchema,
  InvitationActivateRequest,
  InvitationActivateResponse,
  InvitationApiError,
  InvitationValidationResponse,
} from './types';

type ApiState<T> = {
  data?: T;
  error?: InvitationApiError;
  isLoading: boolean;
};

const INVITATION_API_BASE = '/api/Invitation';

const ANTI_FORGERY_TOKEN_KEY = '__RequestVerificationToken';

const getAntiForgeryToken = (): string | undefined => {
  const tokenInput = document.querySelector<HTMLInputElement>(`input[name="${ANTI_FORGERY_TOKEN_KEY}"]`);
  return tokenInput?.value || undefined;
};

class InvitationApiRequestError extends Error {
  payload: InvitationApiError;

  constructor(payload: InvitationApiError) {
    super(payload.message || payload.error || 'Invitation API request failed');
    this.payload = payload;
  }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
  let parseFailed = false;
  const payload = await response.json().catch(() => {
    parseFailed = true;
    return {};
  });
  if (!response.ok) {
    throw new InvitationApiRequestError({
      ...payload,
      status: response.status,
    });
  }

  // A 2xx whose body is not our JSON contract — an auth/login HTML page, a CDN/error/stale-bundle
  // page, or a redirect that resolved to a non-API page. The server guarantees a JSON body on 200,
  // so this only fires for non-contract responses that never reached our action. Surface it
  // distinctly (with the content-type + final redirected URL) instead of returning an empty `{}`
  // the caller would misread as a valid-but-empty response — e.g. an ActivationOptions with no
  // `type`, which the error report otherwise buckets opaquely as `Fallback:EmptyType`.
  if (parseFailed) {
    throw new InvitationApiRequestError({
      error: 'NonJsonResponse',
      status: response.status,
      contentType: response.headers?.get?.('content-type') || undefined,
      url: response.url || undefined,
    });
  }

  return payload as T;
};

// `intent` is forwarded only so the server can stamp it on the re-auth callback URL (a 401
// `ReauthRequired` bounces the browser through an IdP challenge and must land back on the same view).
// Servers predating that contract ignore the unknown parameter.
const getQueryUrl = (path: string, code: string, baseUrl = '', intent?: string): string => {
  const url = `${baseUrl}${INVITATION_API_BASE}/${path}?code=${encodeURIComponent(code)}`;
  return intent ? `${url}&intent=${encodeURIComponent(intent)}` : url;
};

const appendFormFields = (params: URLSearchParams, value: unknown, keyPrefix?: string): void => {
  if (value === undefined || value === null || !keyPrefix) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendFormFields(params, item, `${keyPrefix}[${index}]`);
    });
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      appendFormFields(params, nestedValue, `${keyPrefix}.${key}`);
    });
    return;
  }

  params.append(keyPrefix, String(value));
};

const toFormUrlEncodedBody = (body: object, antiForgeryToken?: string): string => {
  const params = new URLSearchParams();

  if (antiForgeryToken) {
    params.append(ANTI_FORGERY_TOKEN_KEY, antiForgeryToken);
  }

  Object.entries(body).forEach(([key, value]) => {
    appendFormFields(params, value, key);
  });

  return params.toString();
};

const postJson = async <TResponse>(path: string, body: object): Promise<TResponse> => {
  const antiForgeryToken = getAntiForgeryToken();
  if (!antiForgeryToken) {
    throw new InvitationApiRequestError({
      error: 'MissingAntiForgeryToken',
      message: 'Missing __RequestVerificationToken in the page shell. Invitation/Activate cannot be posted safely.',
    });
  }

  const response = await fetch(`${INVITATION_API_BASE}/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: toFormUrlEncodedBody(body, antiForgeryToken),
  });

  const parsed = await parseResponse<TResponse>(response);
  return parsed;
};

const useInvitationGet = <TResponse>(path: string, code?: string, baseUrl = '', intent?: string): ApiState<TResponse> => {
  const [state, setState] = useState<ApiState<TResponse>>({ isLoading: Boolean(code) });

  useEffect(() => {
    let isMounted = true;

    if (!code) {
      setState({ isLoading: false });
      return () => {
        isMounted = false;
      };
    }

    setState({ isLoading: true });

    fetch(getQueryUrl(path, code, baseUrl, intent), { credentials: 'include' })
      .then(response => parseResponse<TResponse>(response))
      .then((data) => {
        if (isMounted) {
          setState({ data, isLoading: false });
        }
      })
      .catch((error: InvitationApiRequestError | Error) => {
        if (isMounted) {
          const apiError = error instanceof InvitationApiRequestError
            ? error.payload
            : { message: error.message || 'Invitation API request failed' };
          setState({ error: apiError, isLoading: false });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [code, path, baseUrl, intent]);

  return state;
};

// `baseUrl` lets the pre-auth landing page (served from the `ui.<env>` campaign-UI host)
// target the CCUI API host (e.g. https://beta.ads-int.microsoft.com) instead of itself.
// When omitted (post-auth flow served from the CCUI host), the request stays same-origin.
export const useInvitationValidate = (code?: string, baseUrl = ''): ApiState<InvitationValidationResponse> => (
  useInvitationGet<InvitationValidationResponse>('Validate', code, baseUrl)
);

export const useInvitationActivationOptions = (code?: string, intent?: string): ApiState<ActivationOptionsResponse> => (
  useInvitationGet<ActivationOptionsResponse>('ActivationOptions', code, '', intent)
);

type RawSelectListItem = {
  Text?: string;
  Value?: string;
  Selected?: boolean;
};

type RawAddressMetadata = {
  Field?: string;
  // Canonical wire field name (e.g. `zipOrPostalCode`), added server-side in PR 6814847
  // so clients don't have to map the legacy `Field` name themselves. Preferred when present.
  WireField?: string;
  Required?: boolean;
  Match?: string | null;
  MaxLength?: number;
  Label?: string;
  Visible?: boolean;
  Order?: number;
};

type RawLoadCountryResponse = {
  value?: Array<{
    countries?: RawSelectListItem[];
    states?: RawSelectListItem[];
    metadata?: RawAddressMetadata[];
  }>;
  error?: string;
};

// The server's generic passthrough regex (accepts any non-control character).
// Treated as "no real validation rule" so the client doesn't reject valid input.
const PASSTHROUGH_MATCH = '[^\\x00-\\x1F]*';

// Fallback map from the legacy metadata `Field` (PascalCase, from the server
// `AddressModel`) -> the wire field name used by `userModel.address`. Only used
// when the server does not provide `WireField` (which is the canonical source
// since PR 6814847). Note `PostalCode` maps to `zipOrPostalCode`.
const METADATA_FIELD_TO_WIRE: Record<string, string> = {
  AddressLine1: 'addressLine1',
  AddressLine2: 'addressLine2',
  AddressLine3: 'addressLine3',
  AddressLine4: 'addressLine4',
  City: 'city',
  StateOrProvince: 'stateOrProvince',
  PostalCode: 'zipOrPostalCode',
  Country: 'country',
};

const mapSelectListItems = (items?: RawSelectListItem[]): { key: string; value: string }[] => (
  (items || [])
    .filter(item => Boolean(item?.Value))
    .map(item => ({ key: item.Value as string, value: item.Text || (item.Value as string) }))
);

const mapAddressMetadata = (items?: RawAddressMetadata[]): Record<string, AddressFieldMetadata> => {
  const result: Record<string, AddressFieldMetadata> = {};
  (items || []).forEach((item) => {
    // Prefer the server-provided canonical `WireField`; fall back to mapping the
    // legacy `Field` name for older server builds that don't return `WireField`.
    const wireField = item?.WireField
      || (item?.Field ? METADATA_FIELD_TO_WIRE[item.Field] : undefined);
    if (!wireField) {
      return;
    }
    const match = item.Match && item.Match !== PASSTHROUGH_MATCH ? item.Match : undefined;
    result[wireField] = {
      field: wireField,
      required: Boolean(item.Required),
      maxLength: typeof item.MaxLength === 'number' ? item.MaxLength : undefined,
      match,
      label: item.Label,
      visible: item.Visible !== false,
      order: item.Order,
    };
  });
  return result;
};

/*
 * Loads the country list, the selected country's state/province list, and the
 * per-field address validation metadata from `GET /api/Invitation/LoadCountry/{code}`.
 *
 * This endpoint lives in the same `InvitationController` as Validate/ActivationOptions/Activate,
 * so it is SAME-ORIGIN to the invitation page (no CORS) and is `[AllowAnonymous]` /
 * `[DisableRequireSelectedCustomerFilter]` \u2014 it needs NO customer context (`cid`/`uid`), so it
 * does not rotate the session cookie / log the invitee out (unlike the CC DomainData endpoints).
 *
 * `getCountries=true` is always sent: the country list is identical per request, and re-sending
 * it on country change keeps the client simple. Response shape: `{ value: [{ countries, states,
 * metadata }] }` where countries/states are MVC `SelectListItem` (`Text`/`Value`/`Selected`) and
 * each metadata item carries the canonical `WireField` (e.g. `zipOrPostalCode`) used for binding.
 */
export const useInvitationLoadCountry = (countryCode?: string): ApiState<CountryStateSchema> => {
  const [state, setState] = useState<ApiState<CountryStateSchema>>({ isLoading: Boolean(countryCode) });

  useEffect(() => {
    let isMounted = true;

    if (!countryCode) {
      setState({ isLoading: false });
      return () => {
        isMounted = false;
      };
    }

    setState(current => ({ ...current, isLoading: true }));

    const url = `${INVITATION_API_BASE}/LoadCountry/${encodeURIComponent(countryCode)}?getCountries=true`;

    fetch(url, { credentials: 'same-origin' })
      .then(response => parseResponse<RawLoadCountryResponse>(response))
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        const entry = Array.isArray(payload?.value) ? payload.value[0] : undefined;
        setState({
          data: {
            countries: mapSelectListItems(entry?.countries),
            states: mapSelectListItems(entry?.states),
            metadata: mapAddressMetadata(entry?.metadata),
          },
          isLoading: false,
        });
      })
      .catch((error: InvitationApiRequestError | Error) => {
        if (!isMounted) {
          return;
        }

        const apiError = error instanceof InvitationApiRequestError
          ? error.payload
          : { message: error.message || 'LoadCountry request failed' };
        setState({ error: apiError, isLoading: false });
      });

    return () => {
      isMounted = false;
    };
  }, [countryCode]);

  return state;
};

export const useInvitationActivate = () => {
  const [state, setState] = useState<ApiState<InvitationActivateResponse>>({ isLoading: false });

  const activate = useCallback(async (request: InvitationActivateRequest) => {
    setState({ isLoading: true });

    try {
      const data = await postJson<InvitationActivateResponse>('Activate', request);

      // Invitation/Activate should return a JSON payload with at least one identity/access field.
      // If we get a fast 200 with an empty/non-JSON body, surface it as an explicit client error
      // instead of treating it as success with {}.
      if (!data || Object.keys(data).length === 0) {
        throw new InvitationApiRequestError({
          status: 200,
          error: 'EmptyResponseBody',
          message: 'Invitation/Activate returned 200 with an empty response body. Expected JSON (e.g., customerId/username/redirectUrl).',
        });
      }

      if (data.customerId === undefined && !data.username && !data.redirectUrl) {
        throw new InvitationApiRequestError({
          status: 200,
          error: 'MalformedSuccessPayload',
          message: 'Invitation/Activate returned success but no expected fields (customerId/username/redirectUrl) were present.',
        });
      }

      setState({ data, isLoading: false });
      return data;
    } catch (error) {
      const apiError = error instanceof InvitationApiRequestError
        ? error.payload
        : { message: error instanceof Error ? error.message : 'Invitation API request failed' };

      setState({ error: apiError, isLoading: false });
      throw error;
    }
  }, []);

  return {
    ...state,
    activate,
  };
};
